import { zodResolver } from '@hookform/resolvers/zod'
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@styled-icons/material-outlined'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import authService from '@/api/auth.service'
import Alert from '@/components/Alert'
import Button from '@/components/Button'
import TextField from '@/components/TextField'

import * as S from './styles'

const errorMapping: { [key: string]: string } = {
  'auth/invalid-email': 'You have entered an invalid e-mail or password',
  'auth/user-not-found': 'You have entered an invalid e-mail or password',
  'auth/wrong-password': 'You have entered an invalid e-mail or password',
  'auth/weak-password':
    'Weak password, password must to be 6 characters or more',
  'auth/email-already-in-use': 'You have entered an invalid e-mail or password',
  'auth/too-many-requests':
    'Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later.',
  'auth/operation-not-allowed': 'Server error, please try again later.',
  'auth/user-disabled': 'Account disabled, please contact support.',
  'auth/network-request-failed':
    'Network error, please check your connection and try again.',
}

const authUserCredentialFormSchema = z.object({
  email: z.string().email().min(1, 'Email is required'),
  password: z.string().min(1, 'Password is required'),
})

type AuthUserCredentialsFormData = z.infer<typeof authUserCredentialFormSchema>

const FormSignIn = () => {
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const { handleSubmit, watch, control } = useForm<AuthUserCredentialsFormData>(
    {
      resolver: zodResolver(authUserCredentialFormSchema),
      defaultValues: {
        email: '',
        password: '',
      },
    }
  )

  const emailField = watch('email')

  const { mutateAsync: signIn, isPending: isLoading } = useMutation({
    mutationFn: authService.login,
    networkMode: 'always',
  })

  const handleLogin = async ({
    email,
    password,
  }: AuthUserCredentialsFormData) => {
    try {
      setError('')
      await signIn({ email: email.toLowerCase(), password })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const getErrorMessage = error?.message?.match(/\(([^)]+)\)/) || []

      setError(
        getErrorMessage[1] ||
          error.message ||
          error.code ||
          'auth/invalid-email'
      )
    }
  }

  return (
    <S.Wrapper onSubmit={handleSubmit(handleLogin)}>
      {!!error && <Alert variant="error">{errorMapping[error] ?? error}</Alert>}

      <Controller
        name="email"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            data-testid="sign-in-email"
            type="email"
            label="Email"
            labelFor="email"
            id="email"
            disabled={isLoading}
            error={error?.message}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />

      <Controller
        name="password"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            data-testid="sign-in-password"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            labelFor="password"
            id="password"
            disabled={isLoading}
            error={error?.message}
            iconPosition="right"
            icon={
              <S.ButtonShowPassword
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                icon={showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                minimal
              />
            }
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />

      <Button type="submit" loading={isLoading} disabled={isLoading} fullwidth>
        Enter
      </Button>

      <S.ButtonForgot
        to={{
          pathname: '/forgot-password',
          search: emailField ? `?email=${emailField}` : '',
        }}
      >
        Forgot password?
      </S.ButtonForgot>
    </S.Wrapper>
  )
}

export default FormSignIn
